/**
 * Un histogramme journalier, en SVG et sans dépendance.
 *
 * **Pourquoi pas une bibliothèque de graphiques.** docs/02 arbitre la pile et
 * n'en prévoit aucune ; en ajouter une passe par une ADR. Ce qu'on dessine ici
 * est une série de barres sur un axe régulier, sans zoom, sans légende, sans
 * animation — Recharts pèserait cent fois ce fichier pour le même résultat, et
 * il faudrait le suivre pour la vie du projet. C'est le même arbitrage que le
 * PDF du certificat côté serveur : quand la question est fermée et petite, la
 * traiter vaut mieux que l'outiller.
 *
 * **Ce qui le rend lisible plutôt que joli** :
 *
 * - des **barres** et non une ligne. Une ligne interpole entre deux points et
 *   affirme donc une valeur les jours intermédiaires ; sur une série de
 *   comptages journaliers, ce lissage est faux par construction.
 * - une barre par jour, **y compris les jours à zéro** — le serveur les envoie
 *   pour cela. Un jour sans barre se lirait comme un jour sans donnée.
 * - un `<title>` par barre, donc une infobulle native **et** un texte lu par un
 *   lecteur d'écran. Le graphique porte `role="img"` et un `aria-label` qui
 *   résume la série : personne ne doit dépendre de la vue pour connaître le
 *   total.
 */
interface Point {
  day: string;
  count: number;
}

interface Props {
  points: Point[];
  label: string;
  /** Déjà traduit et formaté : ce composant n'appelle ni `t()` ni `Intl`. */
  describe: (point: Point) => string;
}

const HEIGHT = 48;
const GAP = 1;

export function SparkBars({ points, label, describe }: Props) {
  if (points.length === 0) {
    return null;
  }
  const peak = Math.max(...points.map((point) => point.count));
  const width = points.length * (4 + GAP);

  return (
    <svg
      viewBox={`0 0 ${width} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="h-12 w-full"
      role="img"
      aria-label={label}
    >
      {points.map((point, index) => {
        // Une hauteur minimale de 1 pour les jours à zéro : sans elle, la barre
        // disparaît et le jour semble absent de la série. Le pic à zéro est le
        // cas limite — tout est plat, et c'est la vérité.
        const height = peak === 0 ? 1 : Math.max(1, Math.round((point.count / peak) * HEIGHT));
        return (
          <rect
            key={point.day}
            x={index * (4 + GAP)}
            y={HEIGHT - height}
            width={4}
            height={height}
            className="fill-brand-500 dark:fill-brand-400"
          >
            <title>{describe(point)}</title>
          </rect>
        );
      })}
    </svg>
  );
}
